import { Link } from "react-router-dom";
import { MonoLabel, Em, Card } from "@/site/components/Atoms";
import Motif from "@/site/anim/Motif";

const SOLUTIONS = [
  {
    n: "01",
    t: "Opportunity Mapping",
    motif: "scan",
    paragraphs: [
      "Forealco identifies where an opportunity exists, why it matters, and whether it has a credible commercial basis. This includes market signals, sector relevance, institutional context, trade logic, local constraints, partner availability, and the practical conditions required for the opportunity to become actionable.",
      "Opportunity mapping is not a generic market overview. It is the first step in understanding whether the opportunity can support a real business route.",
    ],
    focus: ["Market signals", "Commercial relevance", "Route potential"],
  },
  {
    n: "02",
    t: "Market Entry Structuring",
    motif: "structure",
    paragraphs: [
      "Forealco shapes practical entry models for businesses evaluating Kosovo or connected cross-border routes. This may include local presence needs, representation logic, company or partner structure, phased market testing, banking and accounting considerations, documentation flows, and operational readiness.",
      "Market entry is not treated as company formation alone. The purpose is to define how the business will operate, communicate, contract, report, deliver, and remain credible in the target market.",
    ],
    focus: ["Entry model", "Local presence", "Operational readiness"],
  },
  {
    n: "03",
    t: "Local Coordination",
    motif: "cluster",
    paragraphs: [
      "Forealco coordinates selected local support layers where required. This may include advisory, legal, accounting, office, operational, technology, commercial, institutional, or sector-specific support depending on the opportunity.",
      "Local coordination is not a contact list. It is the alignment of relevant actors around a defined route, with clear roles, responsibilities, and practical follow-through.",
    ],
    focus: ["Advisory support", "Local execution", "Role alignment"],
  },
  {
    n: "04",
    t: "Business Infrastructure Enablement",
    motif: "grid",
    paragraphs: [
      "Some opportunities require more than advisory coordination. They require business infrastructure: digital workflows, documentation flows, advisory-office tools, local presence support, ERP-linked processes, financial-data coordination, operational systems, or practical settlement support.",
      "Where relevant, Forealco may coordinate with specialized brands within the wider Erenown ecosystem, including Hubinall and Hangonin, to support digital workflows, advisory-office tools, local presence, company setup support, document coordination, and practical operational needs.",
      "This layer helps connect strategy with execution infrastructure.",
    ],
    focus: ["Digital workflows", "Local presence support", "Operational infrastructure"],
  },
  {
    n: "05",
    t: "Network Development",
    motif: "selected",
    paragraphs: [
      "Forealco helps businesses build controlled and practical local networks around a specific opportunity. This may include advisory channels, commercial contacts, technology partners, institutional pathways, trade relationships, implementation partners, or sector-specific support providers.",
      "The value of a network is not the number of names it contains. The value is whether the right actors can support the route, reduce uncertainty, and help the opportunity move toward execution.",
    ],
    focus: ["Advisory channels", "Commercial contacts", "Institutional pathways"],
  },
  {
    n: "06",
    t: "Market Development Support",
    motif: "wave",
    paragraphs: [
      "Forealco supports selected businesses with opportunity tracking, structured follow-up, coordination support, route refinement, phased growth planning, and practical market development activities.",
      "This solution area is relevant where an opportunity cannot be completed in one step. Some routes require testing, partner development, institutional monitoring, proposal preparation, local validation, or gradual movement from interest to activation.",
    ],
    focus: ["Opportunity tracking", "Structured follow-up", "Phased development"],
  },
];

export default function Solutions() {
  return (
    <>
      {/* HERO */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 pt-16 lg:pt-20">
        <div className="fade-up">
          <MonoLabel>SOLUTIONS</MonoLabel>
        </div>
        <h1 className="h-display fade-up delay-1 mt-6 text-[44px] sm:text-[58px] lg:text-[68px] max-w-5xl">
          Structured solution areas <br className="hidden md:block" />
          for <Em>market opportunities.</Em>
        </h1>
        <p className="fade-up delay-2 mt-7 max-w-2xl text-[15px] leading-[1.85] text-white/70">
          Forealco helps businesses move from market interest to commercially relevant structure
          through connected solution areas. These are not fixed service packages. They are
          practical support areas used to assess, shape, coordinate, and develop opportunities
          where market access, local structure, partner alignment, and execution logic matter.
        </p>
      </section>

      {/* SOLUTION CARDS */}
      <section className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-20 grid grid-cols-1 lg:grid-cols-2 gap-3">
        {SOLUTIONS.map(({ n, t, motif, paragraphs, focus }) => (
          <Card key={n} data-testid={`solution-${n}`} className="group flex flex-col">
            <div className="p-8 pb-7 flex-1">
              <MonoLabel>{n}</MonoLabel>
              <div className="mt-4 font-display text-[26px] md:text-[28px] leading-[1.2]">
                {t}
              </div>

              <div className="mt-5 space-y-4 text-[13.5px] leading-[1.85] text-white/70">
                {paragraphs.map((p, i) => (
                  <p key={i}>{p}</p>
                ))}
              </div>

              <div className="mt-7 flex flex-wrap gap-2">
                {focus.map((f) => (
                  <span
                    key={f}
                    className="text-[10px] tracking-[0.25em] uppercase font-mono text-white/70 border border-white/12 px-3 py-1.5"
                  >
                    {f}
                  </span>
                ))}
              </div>
            </div>

            <div
              className="border-t border-white/10 px-2 pt-2 pb-2 bg-white/[0.015]"
              aria-hidden="true"
            >
              <div className="h-[150px] w-full">
                <Motif kind={motif} height={150} />
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* SOLUTION CONTEXT */}
      <SolutionContext />

      {/* NEED TO STRUCTURE */}
      <section
        data-testid="solutions-need-cta"
        className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28"
      >
        <div className="border border-white/10 bg-white/[0.025] p-10 md:p-14 relative overflow-hidden">
          <div
            className="absolute inset-x-0 top-0 h-px"
            style={{
              background:
                "linear-gradient(90deg, transparent, #9DB4FF 50%, transparent)",
              opacity: 0.5,
            }}
            aria-hidden="true"
          />
          <MonoLabel>NEED TO STRUCTURE AN OPPORTUNITY?</MonoLabel>
          <h2 className="h-display mt-5 text-[30px] md:text-[42px] leading-[1.12] max-w-4xl">
            Define the structure <Em>before moving forward.</Em>
          </h2>
          <p className="mt-6 max-w-2xl text-[15px] leading-[1.9] text-white/70">
            Forealco can help define what the opportunity is, which route is credible, what support
            layers are required, and what the next practical step should be.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <Link
              to="/explore-opportunities"
              data-testid="solutions-cta-primary"
              className="btn-primary"
            >
              Explore Opportunities →
            </Link>
            <Link
              to="/network"
              data-testid="solutions-cta-secondary"
              className="btn-secondary"
            >
              Our Network
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

/* ───────────────── Solution Context ───────────────── */
function SolutionContext() {
  return (
    <section
      data-testid="solutions-context"
      className="max-w-[1500px] mx-auto px-6 lg:px-10 mt-28"
    >
      <div className="grid grid-cols-12 gap-10">
        <div className="col-span-12 lg:col-span-5">
          <MonoLabel>HOW THESE SOLUTIONS WORK</MonoLabel>
          <h2 className="h-display mt-5 text-[34px] md:text-[44px] leading-[1.08]">
            Connected support areas, <Em>not isolated services.</Em>
          </h2>
        </div>
        <div className="col-span-12 lg:col-span-7 lg:pl-6 space-y-5 text-[15px] leading-[1.9] text-white/72 max-w-3xl">
          <p>
            Forealco's solution areas are designed to work together. A business may begin with
            opportunity mapping, then require market entry structuring, local coordination,
            business infrastructure, network development, or phased market development support.
          </p>
          <p>
            The right combination depends on the opportunity. A digital product, a trade route, a
            public modernization project, a defense-related support opportunity, an energy
            infrastructure concept, or an advisory-led market entry case will not require the same
            path.
          </p>
          <p>
            Forealco evaluates what is needed before defining the structure. The objective is not
            to sell every service, but to identify which support layers are required for the
            opportunity to become commercially realistic and operationally grounded.
          </p>

          <div className="pt-4 flex items-center gap-3 flex-wrap">
            <Link
              to="/explore-opportunities"
              data-testid="solutions-context-cta-primary"
              className="btn-primary"
            >
              Explore Opportunities →
            </Link>
            <Link
              to="/network"
              data-testid="solutions-context-cta-secondary"
              className="btn-secondary"
            >
              See Our Network
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
